import {
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Text,
  useDisclosure,
  useToast,
} from '@cardboard-ui/react';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { t } from '@lingui/macro';
import { NavigateBackIcon, NavigateForwardIcon } from 'components/icons';
import { createDirectionalIcon } from 'components/icons/createIcon';
import React, { useCallback, useEffect, useState } from 'react';
import { graphql, useMutation } from 'utils/graphClient';
import { useFileUploader } from 'utils/upload';
import { SignersSteps_sign_Mutation } from './__generated__/SignersSteps_sign_Mutation.graphql';
import { ManageSignatureCertificateModal } from './ManageSignatureCertificateModal';
import { postMessageWTC } from '../utils';
import { SignersSteps_updateSigningData_Mutation } from './__generated__/SignersSteps_updateSigningData_Mutation.graphql';
import { useAuthenticatedSession, useFeatureFlag } from 'utils/sessionProvider';
import { SigningData } from 'types';

const BadgeCheckIcon = createDirectionalIcon(solid('badge-check'));

const signMutation = graphql`
  mutation SignersSteps_sign_Mutation(
    $signatureId: ID!
    $envelopeId: ID!
    $signedFile: FileInput!
    $knownSignatureCount: Int!
  ) {
    sign(
      input: {
        signatureId: $signatureId
        envelopeId: $envelopeId
        signedFile: $signedFile
        knownSignatureCount: $knownSignatureCount
      }
    ) {
      signatureEnvelopeUpdated {
        id
        status
        # requesting FileRenderer_node will force the wrapping modal to reload
        signableNode {
          __typename
          id
          ...ItemViewer_node
        }
        currentFile {
          id
          name
          url
          size
          mimeType
        }
      }
    }
  }
`;

const updateSigningDataMutation = graphql`
  mutation SignersSteps_updateSigningData_Mutation($data: String!) {
    updateSigningData(input: { data: $data }) {
      sessionInfo {
        id
        signingData {
          signatures
          initials
        }
      }
    }
  }
`;

interface SignersStepsProps {
  envelopeId: string;
  isSignatureSigned: boolean;
  currentSignatureId: string;
  completedSignatureCount: number;
  currentFileUrl: string;

  isSigningStarted: boolean;
  setIsSigningStarted: (isSigningStarted: boolean) => void;
}

export const SignersSteps = ({
  envelopeId,
  isSignatureSigned,
  currentSignatureId,
  completedSignatureCount,
  currentFileUrl,
  isSigningStarted,
  setIsSigningStarted,
}: SignersStepsProps) => {
  const isPersistSavedSignaturesEnabled = useFeatureFlag(
    'FEATURE_PERSIST_SAVED_SIGNATURES',
  );
  const toast = useToast();
  const { signingData, member } = useAuthenticatedSession();

  const { uploadFile, completedFiles } = useFileUploader('FILES', {
    maxNumberOfFiles: 1,
    minNumberOfFiles: 1,
    allowedFileTypes: ['application/pdf'],
  });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);

  const [isFilled, setIsFilled] = useState(false);
  const [isCompleted, setIsCompleted] = useState(isSignatureSigned);

  const [knownSignatureCount, setKnownSignatureCount] = useState(0);
  const [signaturePosition, setSignaturePosition] = useState(-1); // Initially no signature is focused

  const [signDigitally, isSigningDigitally] =
    useMutation<SignersSteps_sign_Mutation>(signMutation);

  const [updateSigningData] =
    useMutation<SignersSteps_updateSigningData_Mutation>(
      updateSigningDataMutation,
    );

  const signDocument = useCallback(
    (data: { fileName: string; fileBuffer: Buffer }) => {
      const { fileBuffer, fileName } = data;
      const file = new File([fileBuffer], fileName, {
        type: 'application/pdf',
      });

      uploadFile(file);
    },
    [],
  );

  const observeWebViewerMessages = useCallback((event: any) => {
    if (event.data.type === 'crdbrd-ctow-documentLoaded') {
      setIsDocumentLoaded(true);
    }

    if (event.data.type === 'crdbrd-ctow-signatureCountChanged') {
      setKnownSignatureCount(event.data.signatureCount);
    }

    if (event.data.type === 'crdbrd-ctow-signaturesFilled') {
      setIsFilled(event.data.signaturesFilled);
    }

    if (event.data.type === 'crdbrd-ctow-signaturesLocked') {
      signDocument(event.data);
    }

    if (
      event.data.type === 'crdbrd-ctow-signaturesSaved' &&
      isPersistSavedSignaturesEnabled
    ) {
      const emptySignatures = {
        signatures: '',
        initials: '',
      };

      updateSigningData({
        variables: {
          data: event.data.signatures || JSON.stringify(emptySignatures),
        },
      });
    }
  }, []);

  useEffect(() => {
    if (completedFiles.length > 0) {
      signDigitally({
        variables: {
          envelopeId,
          knownSignatureCount: completedSignatureCount,
          signatureId: currentSignatureId,
          signedFile: {
            uploadToken: completedFiles[0].meta.uploadToken || '',
          },
        },
        onCompleted: (payload) => {
          setIsSigningStarted(false);
          setIsCompleted(true);
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message,
            status: 'error',
          });
        },
      });
    }
  }, [completedFiles.length, envelopeId]);

  useEffect(() => {
    window.addEventListener('message', observeWebViewerMessages);
    return () =>
      window.removeEventListener('message', observeWebViewerMessages);
  }, [observeWebViewerMessages]);

  useEffect(() => {
    if (isPersistSavedSignaturesEnabled && isDocumentLoaded && signingData) {
      loadSavedSignatures(signingData);
    }
  }, [isPersistSavedSignaturesEnabled, isDocumentLoaded, signingData]);

  const handleStart = useCallback(() => {
    loadCurrentMember(member);
    startSigning();
    jumpToSignature({
      signaturePosition: signaturePosition + 1,
      signatureCount: knownSignatureCount,
      setSignaturePosition,
    });
    setIsSigningStarted(true);
  }, [knownSignatureCount]);

  if (isCompleted) {
    return (
      <HStack>
        <BadgeCheckIcon color="green" />
        <Text>{t`You signed this document.`}</Text>
      </HStack>
    );
  }

  if (isSigningStarted) {
    return (
      <HStack spacing={4}>
        <ButtonGroup size="xs" spacing={1}>
          <IconButton
            icon={<NavigateBackIcon />}
            aria-label={t`Previous signature`}
            isDisabled={signaturePosition <= 0}
            onClick={() =>
              jumpToSignature({
                signaturePosition: signaturePosition - 1,
                signatureCount: knownSignatureCount,
                setSignaturePosition,
              })
            }
          />
          <IconButton
            icon={<NavigateForwardIcon />}
            aria-label={t`Next signature`}
            isDisabled={signaturePosition >= knownSignatureCount - 1}
            onClick={() =>
              jumpToSignature({
                signaturePosition: signaturePosition + 1,
                signatureCount: knownSignatureCount,
                setSignaturePosition,
              })
            }
          />
        </ButtonGroup>

        <Text>
          {t`Fields to complete (${
            signaturePosition + 1
          }/${knownSignatureCount})`}
        </Text>

        <ButtonGroup pl={4} size="xs" variant="solid">
          <Button
            colorScheme="blue"
            onClick={() => {
              onOpen();
            }}
            isLoading={isSigningDigitally}
            isDisabled={!isFilled}
          >{t`Complete`}</Button>

          <Button
            onClick={() => {
              cancelSigning();
              setIsSigningStarted(false);
            }}
          >{t`Cancel`}</Button>
        </ButtonGroup>

        <ManageSignatureCertificateModal
          isOpen={isOpen}
          onClose={onClose}
          currentFileUrl={currentFileUrl}
        />
      </HStack>
    );
  }

  return (
    <HStack>
      <Text>{t`Your signature is requested.`}</Text>
      <Button
        size="xs"
        variant="solid"
        colorScheme="blue"
        isDisabled={!isDocumentLoaded}
        onClick={handleStart}
      >{t`Start`}</Button>
    </HStack>
  );
};

interface JumpToSignatureArgs {
  signaturePosition: number;
  signatureCount: number;
  setSignaturePosition: (signaturePosition: number) => void;
}

const jumpToSignature = ({
  signaturePosition,
  signatureCount,
  setSignaturePosition,
}: JumpToSignatureArgs) => {
  if (0 <= signaturePosition && signaturePosition < signatureCount) {
    postMessageWTC({
      type: 'jumpToField',
      position: signaturePosition,
    });
    setSignaturePosition(signaturePosition);
  }
};

const loadCurrentMember = (member: { id: string; name: string }) => {
  postMessageWTC({
    type: 'mapUserIdToDisplayAuthor',
    members: [member],
  });
};

const loadSavedSignatures = (signingData: SigningData) => {
  postMessageWTC({
    type: 'loadSavedSignatures',
    ...signingData,
  });
};

const startSigning = () => {
  postMessageWTC({
    type: 'startSigning',
  });
};

const cancelSigning = () => {
  postMessageWTC({
    type: 'cancelSigning',
  });
};
